import analytics from './analytics';
import globalTime from './globalTime';

export async function delay(ms: number): Promise<void> {
  if (ms === 0) return;
  return new Promise((res) => setTimeout(res, ms));
}

export type RetryOptions = {
  retryCount?: number;
  linearBackoff?: number;
  retryOnlyIf?: (error: any) => boolean;
};

export type RetryError = { error: Error; requestTries: number };
export type TimedError = { error: Error; requestMs: number };
export type TimedRetryError = RetryError & TimedError;

export class TimeoutError extends Error {
  code = 'timeout' as const;

  constructor(message: string) {
    super(message);
  }
}

export function createResolvablePromise<T = void>(): {
  resolve: (value: T) => void;
  reject: (error: any) => void;
  promise: Promise<T>;
} {
  let resolve: (value: T) => void;
  let reject: (error: any) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { reject, resolve, promise };
}

/**
 * @throws `TimeoutError` if `promise` doesn't resolve in `timeoutMs`.
 * @returns result of `promise`.
 */
export function promiseTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<T> {
  let timer: number | undefined;
  const timeoutPromise = new Promise<any>((_, reject) => {
    timer = window.setTimeout(
      () => reject(new TimeoutError('Action timed out')),
      timeoutMs,
    );
  });

  return Promise.race([promise, timeoutPromise])
    .then((result) => {
      clearTimeout(timer);
      return result;
    })
    .catch((error) => {
      clearTimeout(timer);
      return Promise.reject(error);
    });
}

export async function retry<T>(
  action: () => Promise<T>,
  options?: RetryOptions,
): Promise<{ result: T; requestTries: number }> {
  const retryCount = options?.retryCount ?? 2;

  const backoff = options?.linearBackoff ?? 500;
  let currentRetry = 0;

  do {
    try {
      return { result: await action(), requestTries: currentRetry + 1 };
    } catch (error) {
      // if retryOnlyIf is defined and we don't qualify for retries, throw immediately.
      if (options?.retryOnlyIf && !options.retryOnlyIf(error)) {
        return Promise.reject({ error, requestTries: currentRetry + 1 });
      }

      if (currentRetry >= retryCount) {
        return Promise.reject({ error, requestTries: currentRetry + 1 });
      } else {
        currentRetry++;
        await delay((currentRetry - 1) * backoff);
      }
    }
  } while (true); // eslint-disable-line no-constant-condition
}

export async function withTiming<T>(
  action: () => Promise<T>,
): Promise<{ result: T; requestMs: number }> {
  const t0 = performance.now();

  try {
    const result = await action();
    return { result, requestMs: performance.now() - t0 };
  } catch (error) {
    return Promise.reject({ error, requestMs: performance.now() - t0 });
  }
}

export async function retryWithTiming<T>(
  action: () => Promise<T>,
  options?: RetryOptions,
): Promise<{ result: T; requestMs: number; requestTries: number }> {
  try {
    const {
      result: { result, requestTries },
      requestMs,
    } = await withTiming(() => retry(action, options));

    return { result, requestMs, requestTries };
  } catch ({ error: { error, requestTries }, requestMs }) {
    return Promise.reject({ error, requestMs, requestTries });
  }
}

/**
 * Options for automatically-tracked platform API fetches.
 */
export type TrackedRequestOptions<Body> = {
  /**
   * The HTTP method, defaults to 'GET'
   */
  method?: string;

  /**
   * A map of headers.
   */
  headers?: Record<string, string>;

  /**
   * The request body where the type can be provided by the requesting function
   */
  body?: Body;

  /**
   * The maximum number of attempts to try before giving up.
   */
  maxAttempts?: number;

  /**
   * Whether the request should be retried, based on the error encountered.
   */
  retryError?: (error: Error) => Promise<boolean>;

  /**
   * Whether the request should be retried, based on the response received.
   */
  retryResponse?: (response: Response) => Promise<boolean>;

  /**
   * How long to wait between attempts. Defaults to a basic quadratic exponential backoff starting at 200ms.
   */
  retryDelay?: (attempt: number) => number;

  /**
   * Platform and implementation information to attach to the tracking event.
   */
  tracking: {
    platform: 'LINE';
    source: string;
  };
};

/**
 * Basic implementation of exponential backoff for use by tracked requests.
 */
function defaultExponentialBackoff(attempts: number) {
  const base = 100; // milliseconds
  const backoff = Math.pow(2, attempts - 1); // series: 1, 2, 4, 8, 16, etc
  return base * backoff;
}

/**
 * Default conditions for retrying a request: always retry errors.
 */
async function defaultShouldRetryError(): Promise<boolean> {
  return true;
}

/**
 * Default conditions for retrying a request: if a response is not OK.
 */
export async function defaultShouldRetryResponse(
  response: Response,
): Promise<boolean> {
  return [
    408, // Request Timeout
    429, // Too Many Requests
    502, // Bad Gateway
    503, // Service Unavailable
    504, // Gateway Timeout
  ].includes(response.status);
}

export async function trackedRequest(
  fetcher: typeof fetch,
  request: string,
  options: TrackedRequestOptions<BodyInit>,
): Promise<Response> {
  const { headers, body, tracking } = options;
  const method = options.method?.toUpperCase() || 'GET';
  const url = new URL(request);
  const shouldRetryError = options.retryError || defaultShouldRetryError;
  const shouldRetryResponse =
    options.retryResponse || defaultShouldRetryResponse;
  const retryDelay = options.retryDelay || defaultExponentialBackoff;
  const maxAttempts = Math.max(
    Number.isSafeInteger(options.maxAttempts) ? options.maxAttempts : 0,
    1,
  );
  const startTime = performance.now();

  let error: Error | undefined, response: Response | undefined, retry: boolean;
  let attempts = 0;

  do {
    attempts += 1;

    // reset before every attempt
    error = response = undefined;

    try {
      response = await fetcher(url.href, { method, headers, body });
      retry = await shouldRetryResponse(response);
    } catch (err) {
      error = err;
      retry = await shouldRetryError(error);
    }

    // if we shouldn't retry, break out of the attempt loop
    if (!retry) break;

    // we're going for another attempt, so add a delay unless we've hit the limit already
    if (attempts < maxAttempts) await delay(retryDelay(attempts));
  } while (attempts < maxAttempts);

  const endTime = performance.now();

  // successful if we didn't want to retry again and the response has a 2xx status
  const success = !retry && response && response.ok;

  let failedResponseText;

  if (!success && response) {
    failedResponseText = await response.text();
    // this can only be called once per response and we've used it up, so replace it
    response.text = async () => failedResponseText;
  }

  analytics.pushEvent('PlatformRequest', {
    platform: tracking.platform,
    requestTries: attempts,
    requestMs: endTime - startTime,
    source: tracking.source,
    method,
    href: url.href,
    origin: url.origin,
    pathname: url.pathname,
    search: url.search,
    statusCode: response?.status,
    statusText: response?.statusText,
    errorName: error?.name,
    errorMessage: error?.message,
    failedResponseText,
    result: success ? 'Success' : 'Failure',
  });

  // the last attempt produced an error, so rethrow that
  if (error) throw error;

  return response;
}

export async function trackedJsonRequest<T extends Record<string, unknown>>(
  fetcher: typeof fetch,
  request: string,
  options: TrackedRequestOptions<T>,
): Promise<Response> {
  return trackedRequest(fetcher, request, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
}
